import moment from 'moment-tz';

import {DOCUMENT_TAG} from 'app/documents/api';
import {LANE_RTK_TAGS} from 'app/lanes/api';
import {sweetAPI} from 'app/store/sweet-api';
import {
  createTagsListFromResponse,
  providesTagsByURL,
  showLoadingBar,
  showMessages,
} from 'app/store/sweet-api/utils';
import {
  getProcedureDetailURLByHashid,
  PROCEDURES_API_URL,
  PROCEDURES_API_URL_COPY,
  PROCEDURES_VERSION_APPROVE_API_URL,
  PROCEDURES_VERSIONS_API_URL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';

import {standardizeRawProcedure} from './utils';

import type {
  ProceduresDashboardLocationParamsType,
  ProceduresFetchParamsType,
} from './types';
import type {CreateNewDocProps} from 'app/general/types';
import type {
  ListResponseType,
  ProcedureHashidSerializedType,
  ProcedureSerializedType,
  ProcedureVersionSerializedType,
  RawProcedureSerializedType,
} from 'sweet-types';

export const PROCEDURE_RTK_TAGS = {
  TAGS: 'PROCEDURE',
  HASHID: 'PROCEDUREHASHID',
  VERSIONS: 'PROCEDUREVERSION',
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [
      DOCUMENT_TAG,
      LANE_RTK_TAGS.TAGS,
      PROCEDURE_RTK_TAGS.TAGS,
      PROCEDURE_RTK_TAGS.HASHID,
      PROCEDURE_RTK_TAGS.VERSIONS,
    ],
  })

  .injectEndpoints({
    endpoints: (build) => ({
      procedureByHashid: build.query<ProcedureHashidSerializedType, string>({
        query: (hashid) => ({
          url: getProcedureDetailURLByHashid(hashid),
        }),
        transformResponse: standardizeRawProcedure,
        providesTags: (response) =>
          response != null
            ? [{type: PROCEDURE_RTK_TAGS.HASHID, id: response.url}]
            : [],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the procedure.'),
        }),
      }),
      procedureById: build.query<ProcedureSerializedType, number>({
        query: (id) => ({
          url: `${PROCEDURES_API_URL}${id}/`,
        }),
        transformResponse: standardizeRawProcedure,
        providesTags: (response) =>
          response != null
            ? [{type: PROCEDURE_RTK_TAGS.TAGS, id: response.url}]
            : [],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the procedure.'),
        }),
      }),
      procedures: build.query<
        ListResponseType<ProcedureSerializedType>,
        | {lane?: string}
        | {
            policy_id: number;
            fields: Array<string>;
          }
        | void
        | ProceduresFetchParamsType
      >({
        query: (params) => ({
          url: PROCEDURES_API_URL,
          params: params ?? {},
        }),
        providesTags: (response) =>
          createTagsListFromResponse(PROCEDURE_RTK_TAGS.TAGS, response, {
            addsListId: true,
            idGetter: (procedure) => procedure.url,
          }),
        transformResponse: (
          response: ListResponseType<RawProcedureSerializedType>
        ) => ({
          ...response,
          results: response.results.map(standardizeRawProcedure),
        }),
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the procedures.'),
        }),
      }),

      createProcedure: build.mutation<
        ProcedureSerializedType,
        CreateNewDocProps
      >({
        query: (data) => ({
          url: PROCEDURES_API_URL,
          method: 'POST',
          body: {
            steps_blocked_at: moment().utc().format(),
            ...data,
          },
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Could not create the procedure.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: DOCUMENT_TAG, id: 'list'},
        ],
      }),

      updateProcedure: build.mutation<
        ProcedureSerializedType,
        {url: string} & Partial<ProcedureSerializedType> & {
            successMessage?: string;
            errorMessage?: string;
          }
      >({
        query: ({url, ...body}) => ({
          url: url,
          method: 'PATCH',
          body,
        }),
        transformResponse: standardizeRawProcedure,
        onQueryStarted: showMessages({
          onSuccess: ({successMessage}) => successMessage ?? null,
          onError: (error, {errorMessage}) =>
            errorMessage ?? gt('Unable to update the procedure'),
        }),
        invalidatesTags: (result, error, {url}) => [
          {type: PROCEDURE_RTK_TAGS.TAGS, id: url},
          {type: PROCEDURE_RTK_TAGS.HASHID, id: url},
        ],
      }),

      procedureVersionById: build.query<
        ProcedureVersionSerializedType,
        number | string
      >({
        query: (id) => ({
          url: `${PROCEDURES_VERSIONS_API_URL}${id}/`,
        }),
        transformResponse: standardizeRawProcedure,
        providesTags: (response) =>
          response != null
            ? [{type: PROCEDURE_RTK_TAGS.VERSIONS, id: response.url}]
            : [],
        onQueryStarted: showMessages({
          onError: () => gt('Could not fetch the version.'),
        }),
      }),

      procedureVersions: build.query<
        ListResponseType<ProcedureVersionSerializedType>,
        {
          procedureId?: string | number;
          page?: string | number;
          page_size?: string;
        }
      >({
        query: () => ({
          url: PROCEDURES_VERSIONS_API_URL,
        }),
        providesTags: providesTagsByURL(PROCEDURE_RTK_TAGS.VERSIONS),
        onQueryStarted: showMessages({
          onError: () => gt('Unable to load the procedure versions.'),
        }),
      }),
      updateProcedureVersion: build.mutation<
        ProcedureVersionSerializedType,
        {url: string} & Partial<ProcedureVersionSerializedType>
      >({
        query: ({url, ...version}) => ({
          url: url,
          method: 'PATCH',
          body: version,
        }),
        transformResponse: standardizeRawProcedure,
        invalidatesTags: (result, error, {url}) => [
          {type: PROCEDURE_RTK_TAGS.VERSIONS, id: url},
        ],
      }),

      approveProcedureVersion: build.mutation<
        unknown,
        {
          versionURL: string;
          documentId: number;
          options: {notify?: boolean; signoff?: boolean};
        }
      >({
        query: ({versionURL, options}) => ({
          method: 'POST',
          url: PROCEDURES_VERSION_APPROVE_API_URL,
          body: {url: versionURL, ...options},
        }),
        onQueryStarted: (args, api) => {
          showMessages({
            onSuccess: ({options}) =>
              options.notify
                ? gt('Approved. Teammates will be notified')
                : gt('This version is now approved and live.'),
            onError: () => gt('Could not approve that version.'),
          })(args, api);
        },
        invalidatesTags: () => [
          {type: LANE_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
          {type: PROCEDURE_RTK_TAGS.HASHID, id: 'list'},
        ],
      }),
      duplicateProcedure: build.mutation<
        ProcedureSerializedType,
        {name: string; url: string; teams?: Array<string>}
      >({
        query: (body) => ({
          url: PROCEDURES_API_URL_COPY,
          method: 'POST',
          body,
        }),
        onQueryStarted: (args, api) => {
          showLoadingBar(args, api);
          showMessages({
            onError: () => gt('Unable to duplicate'),
          })(args, api);
        },
        invalidatesTags: () => [{type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'}],
      }),
    }),
  });

export const {
  useProcedureByHashidQuery: useProcedureByHashid,
  useProcedureByIdQuery: useProcedureById,
  useProceduresQuery: useProcedures,
  useUpdateProcedureMutation: useUpdateProcedure,
  useCreateProcedureMutation: useCreateProcedure,
  useProcedureVersionByIdQuery: useProcedureVersionById,
  useProcedureVersionsQuery: useProcedureVersions,
  useUpdateProcedureVersionMutation: useUpdateProcedureVersion,
  useLazyProceduresQuery: useLazyProcedures,
  useApproveProcedureVersionMutation: useApproveProcedureVersion,
  useDuplicateProcedureMutation: useDuplicateProcedure,
} = extendedAPI;

export const convertLocationParamsToFetchParams = (
  query: ProceduresDashboardLocationParamsType
): ProceduresFetchParamsType => {
  const filters = query.filter != null ? query.filter.split(',') : [];
  return {
    page: query.page != null ? query.page : '1',
    page_size: query.page_size != null ? query.page_size : DEFAULT_PAGE_SIZE,
    search: query.search,
    ordering: query.ordering,
    private:
      filters.indexOf('private') > -1
        ? 'true'
        : filters.indexOf('public') > -1
        ? 'false'
        : undefined,
    tag: query.tag != null ? query.tag : undefined,
    team_id:
      query.team_id != null && query.team_id !== '-1'
        ? query.team_id
        : undefined,
    in_no_teams: query.team_id === '-1' ? '1' : undefined,
    exclude_team_id: query.exclude_team_id,
    visible_to_user:
      query.visible_to_user != null ? query.visible_to_user : undefined,
    exclude_id: query.exclude_id,
    filter: query.filter,
    folder: query.folder,
    quiz_id: query.quiz_id,
  };
};
